import React, { useEffect, useRef, useState } from "react";
import "./ViewTabs.css";

export type ViewMode = "takt" | "export" | "tree" | "schedule" | "pull-plan" | "admin";

interface TabDef {
  id: ViewMode;
  label: string;
}

const primaryTabs: TabDef[] = [
  { id: "takt", label: "Board" },
  { id: "pull-plan", label: "Plan" },
  { id: "export", label: "Compare & Export" },
  { id: "schedule", label: "P6 Schedule" },
];

const overflowTabsBase: TabDef[] = [
  { id: "tree", label: "Tree" },
];

interface Props {
  activeView: ViewMode;
  onChangeView: (view: ViewMode) => void;
  isAdmin?: boolean;
  /** Right-aligned content rendered at the end of the tab bar (e.g. the
   *  project-delta chip). */
  rightSlot?: React.ReactNode;
}

const ViewTabs: React.FC<Props> = ({ activeView, onChangeView, isAdmin, rightSlot }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const overflowTabs = isAdmin
    ? [...overflowTabsBase, { id: "admin" as ViewMode, label: "Admin" }]
    : overflowTabsBase;

  const overflowIds = new Set<ViewMode>(overflowTabs.map((t) => t.id));
  const isOverflowActive = overflowIds.has(activeView);

  // Close on click-outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [menuOpen]);

  const handleOverflowSelect = (view: ViewMode) => {
    onChangeView(view);
    setMenuOpen(false);
  };

  return (
    <div className="view-tabs">
      {primaryTabs.map((tab) => (
        <button
          key={tab.id}
          className={`view-tab ${activeView === tab.id ? "active" : ""}`}
          onClick={() => onChangeView(tab.id)}
        >
          {tab.label}
        </button>
      ))}

      <div className="view-tabs__overflow-wrapper" ref={wrapperRef}>
        <button
          className={`view-tab view-tabs__overflow-btn ${isOverflowActive ? "active" : ""}`}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          ...
        </button>

        {menuOpen && (
          <div className="view-tabs__overflow-menu">
            {overflowTabs.map((tab) => (
              <button
                key={tab.id}
                className={`view-tabs__overflow-item ${activeView === tab.id ? "active" : ""}`}
                onClick={() => handleOverflowSelect(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {rightSlot && <div className="view-tabs__right">{rightSlot}</div>}
    </div>
  );
};

export default ViewTabs;
